import { Grid, useTheme, MenuItem, CircularProgress, ListItemIcon, ListItemText } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useContext, useState, useEffect, useRef } from 'react';
import FormInput from '../../SystemDesign/FormInput';
import FormSelect from '../../SystemDesign/FormSelect';
import MuiDialog from '../../SystemDesign/MuiDialog';
import PhoneInput from '../../SystemDesign/PhoneInput';
import * as yup from 'yup';
import { useGetRolesQuery, usePutUsersUpdateByUserIdMutation } from '../../services/SkillsManagement';
import EditIcon from '@mui/icons-material/Edit';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../../Context/AuthContext';
import { UPDATE_EMPLOYEE } from '../../Reducers/Actions';

const UpdateEmployee = ({ row }) => {
    const user = row.original;
    const theme = useTheme();
    const [putUserUpdate] = usePutUsersUpdateByUserIdMutation();

    const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
    const [close, setClose] = useState(false);
    const { user: authUser } = useContext(AuthContext);
    const dispatch = useDispatch();
    
    // Fetch roles data from API
    const { data: rolesData, isLoading: isLoadingRoles, error: rolesError } = useGetRolesQuery({});
    const roleOptions = rolesData?.roles?.map(role => ({
        id: role.roleId,
        title: role.type
    })) || [];

    const initialValues = {
        username: '',
        email: '',
        roleId: '',
        profile: {
            firstName: '',
            lastName: '',
            phone: '',
            birthday: '',
            gender: '',
            bio: ''
        }
    };

    const [formValues, setFormValues] = useState(initialValues);
    const submitPromiseRef = useRef(null);

    const genderOptions = [
        { id: 'Male', title: "Male" },
        { id: 'Female', title: "Female" }
    ];

    // Initialize form values from the user data
    useEffect(() => {
        if (user) {
            setFormValues({
                username: user.username || '',
                email: user.email || '',
                roleId: user.roleId || '',
                profile: {
                    firstName: user.profile?.firstName || '',
                    lastName: user.profile?.lastName || '',
                    phone: user.profile?.phone || '',
                    birthday: user.profile?.birthday || '',
                    gender: user.profile?.gender || '',
                    bio: user.profile?.bio || ''
                }
            });
        }
    }, [user]);

    // Handle roles error
    useEffect(() => {
        if (rolesError) {
            setInfoAlert({ 
                severity: 'error', 
                message: "Error fetching roles" 
            });
            setOpenSnackBar(true);
        }
    }, [rolesError, setInfoAlert, setOpenSnackBar]);

    const formFields = [
        { type: 'text', name: 'username', label: "Username", required: true },
        { type: 'email', name: 'email', label: "Email", required: true },
        { type: 'text', name: 'profile.firstName', label: "First Name", required: true },
        { type: 'text', name: 'profile.lastName', label: "Last Name", required: true },
        { type: 'phone', name: 'profile.phone', label: "Phone", required: false },
        { type: 'date', name: 'profile.birthday', label: "Birthday", required: false },
        { 
            type: 'select', 
            name: 'profile.gender', 
            label: "Gender", 
            options: genderOptions, 
            required: true 
        },
        { type: 'text', name: 'profile.bio', label: "Bio", required: false },
        { 
            type: 'select', 
            name: 'roleId', 
            label: "Role", 
            options: roleOptions, 
            required: true 
        },
    ];
    
    function getFieldGridProps(field) {
        switch (field.name) {
            case 'username':
            case 'email':
                return { xs: 12, md: 6, lg: 6 };
            case 'profile.firstName':
            case 'profile.lastName':
           
                return { xs: 12, md: 6, lg: 6 };
            case 'profile.birthday':
            case 'profile.gender':
            case 'profile.phone':
                return { xs: 12, md: 4, lg: 4 };
            
           
            case 'roleId':
            case 'profile.bio':
                return { xs: 12, md: 12, lg: 12 };
            default:
                return { xs: 12, md: 6 };
        }
    }

    const formValidationSchema = yup.object().shape({
        username: yup.string()
            .required("Username is required")
            .min(3, "Username must be at least 3 characters"),
        email: yup.string()
            .email("Invalid email format")
            .required("Email is required"),
        roleId: yup.number()
            .required("Role is required"),
        
        profile: yup.object().shape({
            firstName: yup.string()
                .required("First name is required")
                .min(2, "First name must be at least 2 characters"),
            lastName: yup.string()
                .required("Last name is required")
                .min(2, "Last name must be at least 2 characters"),
            phone: yup.string()
                .nullable(),
            birthday: yup.date()
                .nullable()
                .transform((value, originalValue) => originalValue === '' ? null : value),
            gender: yup.string()
                .required("Gender is required")
                .oneOf(['Male', 'Female'], "Gender must be Male or Female"),
            bio: yup.string()
                .nullable()
        })
    });

    const handleChange = (e, formik) => {
        const { name, value } = e.target;
        
        // Handle dot notation for nested fields like 'profile.firstName'
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            formik.setFieldValue(name, value);
        } 
        // Convert roleId to number when it's being changed
        else if (name === 'roleId') {
            const numericValue = Number(value);
            formik.setFieldValue(name, numericValue);
        } 
        // Handle regular fields
        else {
            formik.setFieldValue(name, value);
        }
    };

    const handleSubmit = async (values, actions) => {
        try {
            // Build JSON payload to preserve numeric types
            const payload = {
                username: values.username,
                email: values.email,
                roleId: Number(values.roleId),
                profile: {
                    firstName: values.profile.firstName,
                    lastName: values.profile.lastName,
                    phone: values.profile.phone || undefined,
                    birthday: values.profile.birthday
                        ? (values.profile.birthday instanceof Date
                            ? values.profile.birthday.toISOString()
                            : values.profile.birthday)
                        : undefined,
                    gender: values.profile.gender,
                    bio: values.profile.bio || undefined,
                },
            };

            // Prefer the explicit endpoint with userId in path
            const response = await putUserUpdate({
                userId: user.id,
                type: 'user',
                body: payload,
            }).unwrap();
            
            // Build a safe updated user if backend doesn't return the full user
            // Try to enrich role from rolesData if response lacks role
            const resolvedRole = response?.user?.role || (rolesData?.roles?.find(r => r.roleId === payload.roleId) ? {
                roleId: payload.roleId,
                name: rolesData.roles.find(r => r.roleId === payload.roleId)?.name || rolesData.roles.find(r => r.roleId === payload.roleId)?.type || user.role?.name,
                type: rolesData.roles.find(r => r.roleId === payload.roleId)?.type || user.role?.type,
            } : user.role);

            const updatedUser = response?.user ?? {
                ...user,
                username: payload.username ?? user.username,
                email: payload.email ?? user.email,
                roleId: payload.roleId ?? user.roleId,
                role: resolvedRole,
                profile: {
                    ...user.profile,
                    ...(payload.profile || {}),
                },
            };

            // RTK Query automatically invalidates the cache and refetches
            // No need for Redux dispatch since the mutation has invalidatesTags

            // Success snackbar
            setInfoAlert({
                severity: 'success',
                message: `Employee ${updatedUser.username} updated successfully`,
            });
            setOpenSnackBar(true);
            // resolve promise to let dialog close
            if (submitPromiseRef.current) {
                submitPromiseRef.current(true);
                submitPromiseRef.current = null;
            }
        } catch (error) {
            console.error("Update error:", error);
            setInfoAlert({ 
                severity: 'error', 
                message: error?.data?.message || error?.error || error?.message || "Error updating employee" 
            });
            setOpenSnackBar(true);
            if (submitPromiseRef.current) {
                submitPromiseRef.current(false);
                submitPromiseRef.current = null;
            }
        }
        actions.setSubmitting(false);
    };

    return (
        <MuiDialog
            icon={<EditIcon />}
            triggerButton={
                <MenuItem>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Update</ListItemText>
                </MenuItem>
            }
            title="Update Employee"
            subtitle={`Editing information for ${user.username}`}
            maxWidth="md"
            content={
                <div>
                    {isLoadingRoles ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <Formik
                            initialValues={formValues}
                            validationSchema={formValidationSchema}
                            validateOnChange={false}
                            validateOnBlur={false}
                            enableReinitialize={true}
                            onSubmit={handleSubmit}
                        >
                            {(formik) => (
                                <Form id="updateEmployeeForm" autoComplete="off">
                                    <Grid container spacing={2}>
                                        {formFields.map((field, index) => (
                                            <Grid item size={{...getFieldGridProps(field)}} key={index}>
                                                {field.type === 'text' || field.type === 'email' || field.type === 'date' ? (
                                                    <FormInput
                                                        type={field.type}
                                                        name={field.name}
                                                        label={field.label}
                                                        required={field.required}
                                                        value={field.name.includes('.') 
                                                            ? formik.values.profile[field.name.split('.')[1]]
                                                            : formik.values[field.name]}
                                                        onChange={(e) => handleChange(e, formik)}
                                                        onBlur={formik.handleBlur}
                                                        error={field.name.includes('.') 
                                                            ? (formik.touched.profile && formik.touched.profile[field.name.split('.')[1]] && 
                                                                Boolean(formik.errors.profile && formik.errors.profile[field.name.split('.')[1]]))
                                                            : (formik.touched[field.name] && Boolean(formik.errors[field.name]))}
                                                        helperText={field.name.includes('.')
                                                            ? (formik.touched.profile && formik.touched.profile[field.name.split('.')[1]] && 
                                                                formik.errors.profile && formik.errors.profile[field.name.split('.')[1]])
                                                            : (formik.touched[field.name] && formik.errors[field.name])}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                ) : field.type === 'phone' ? (
                                                    <PhoneInput
                                                        name={field.name}
                                                        label={field.label}
                                                        required={field.required}
                                                        value={formik.values.profile.phone || ''}
                                                        onChange={(e) => handleChange(e, formik)}
                                                        onBlur={formik.handleBlur}
                                                        error={formik.touched.profile && formik.touched.profile.phone && 
                                                            Boolean(formik.errors.profile && formik.errors.profile.phone)}
                                                        helperText={formik.touched.profile && formik.touched.profile.phone && 
                                                            formik.errors.profile && formik.errors.profile.phone}
                                                    />
                                                ) : (
                                                    <FormSelect
                                                        name={field.name}
                                                        label={field.label}
                                                        options={field.options}
                                                        required={field.required}
                                                        value={field.name.includes('.') 
                                                            ? formik.values.profile[field.name.split('.')[1]]
                                                            : formik.values[field.name]}
                                                        onChange={(e) => handleChange(e, formik)}
                                                        onBlur={formik.handleBlur}
                                                        error={field.name.includes('.') 
                                                            ? (formik.touched.profile && formik.touched.profile[field.name.split('.')[1]] && 
                                                                Boolean(formik.errors.profile && formik.errors.profile[field.name.split('.')[1]]))
                                                            : (formik.touched[field.name] && Boolean(formik.errors[field.name]))}
                                                        helperText={field.name.includes('.')
                                                            ? (formik.touched.profile && formik.touched.profile[field.name.split('.')[1]] && 
                                                                formik.errors.profile && formik.errors.profile[field.name.split('.')[1]])
                                                            : (formik.touched[field.name] && formik.errors[field.name])}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    )}
                </div>
            }
            existAction={true}
            actionTitle="Update"
            actionFunction={async () => {
                const formikInstance = document.querySelector('#updateEmployeeForm');
                if (!formikInstance) return false;
                return new Promise((resolve) => {
                    submitPromiseRef.current = resolve;
                    formikInstance.requestSubmit();
                });
            }}
            onClose={close}
        />
    );
};

export default UpdateEmployee;